import { Box, Grid, Stack, styled } from '@mui/material';
import { Dispatch, SetStateAction, useContext, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { Cell } from 'react-table';
import { ClientBulkUploadDTO, getIndustries, IndustryDTO, uploadBulkClients } from 'wisestep-api';
import {
  WsButton,
  WsButtonGroup,
  WsColors,
  WsDataTable,
  WsSelect,
  WsSelectObj,
  WsTypoColors,
  WsTypography,
  WsTypoVariants,
} from 'wisestep-base';

import { failed, success } from '../../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../../utils/NotificationStore';
import { BulkImportContext } from '../../context/BulkImportContext';
import {
  BulkUploadType,
  generateAndDownloadExcel,
  getColumnOptions,
} from '../../helper/ClientUploadHelper';
import { BulkUploadResponseDTO } from '../../types/BulkUploadResponseDTO';

const StyledStack = styled(Stack)(() => ({
  width: 400,
}));

interface ISpreadSheetProps {
  parsedData: ClientBulkUploadDTO[];
  setNewClientPayload: Dispatch<SetStateAction<ClientBulkUploadDTO[]>>;
  uploadType: BulkUploadType;
}

const MapSpreadsheetColumns = ({
  parsedData,
  setNewClientPayload,
  uploadType,
}: ISpreadSheetProps) => {
  const { setBulkImport } = useContext(BulkImportContext);
  const [ignoreFirstRow, setIgnoreFirstRow] = useState(true);
  const setNotification = useNotificationStore((state) => state.setNotification);
  const [industries, setIndustries] = useState<IndustryDTO[]>([]);

  const methods = useForm();
  const navigate = useNavigate();
  const options: WsSelectObj[] = getColumnOptions(uploadType);

  const generateColumnNames = (count: number) =>
    Array.from({ length: count }, (_, index) => `column${index + 1}`);

  const columnNames = generateColumnNames(options.length);

  const columns = Object.keys(parsedData[0])
    .filter((value) => columnNames.includes(value))
    .map((key) => ({
      Header: <WsSelect name={key} label={''} selectArray={options} />,
      accessor: key,
      Cell: (cell: Cell) => (
        <WsTypography color={WsTypoColors.BLACK} variant={WsTypoVariants.BODY1}>
          {cell.value}
        </WsTypography>
      ),
    }));

  useEffect(() => {
    const getIndustryData = async () => {
      const res = await getIndustries();
      setIndustries(res.data);
    };
    getIndustryData();
  }, []);

  const onSubmit = async (data) => {
    const modifiedData: ClientBulkUploadDTO[] = JSON.parse(JSON.stringify(parsedData));

    modifiedData.forEach((client) => {
      for (const key in data) {
        client[data[key]] = client[key];
      }
    });

    modifiedData.forEach((client) => {
      columnNames.forEach((columnName) => delete client[columnName]);
      setNewClientPayload(modifiedData);
    });

    try {
      const finalList = [...modifiedData];
      if (finalList && finalList.length > 0) {
        if (ignoreFirstRow) {
          finalList.shift();
        }

        finalList.forEach((client) => {
          if (client.industry) {
            client.industry =
              industries.find((industry) => industry.label === client.industry)?.value || null;
          }
        });
        const response = await uploadBulkClients(finalList, uploadType);
        if (response?.data) {
          const bulkUploadResponse: BulkUploadResponseDTO<ClientBulkUploadDTO> = response.data.data;
          Object.entries(bulkUploadResponse).forEach(([, records]) => {
            if (Array.isArray(records)) {
              records.forEach((record) => {
                if (record && record.industry) {
                  const industryName = industries.find(
                    (industry) => industry.value?.toString() === record.industry
                  )?.label;
                  if (industryName) {
                    record.industry = industryName;
                  }
                }
              });
            }
          });

          generateAndDownloadExcel<ClientBulkUploadDTO>(
            bulkUploadResponse,
            ['clientStatusDTO', 'markStatus', 'id'],
            options,
            'ClientUploadResults.xlsx'
          );
        }
        setNotification(success(`Client details successfully uploaded.`));
        if (setBulkImport) {
          setBulkImport(response.data);
          navigate('/clients');
        }
      } else {
        setNotification(failed('Please select at least one client to upload.'));
      }
    } catch (e) {
      console.error(e);
      setNotification(failed(`Something went wrong while uploading clients details.`));
    }
  };

  const onChangeRow = (index: number, value: string) =>
    value.toLocaleLowerCase() == 'yes' ? setIgnoreFirstRow(true) : setIgnoreFirstRow(false);

  return (
    <FormProvider {...methods}>
      <Box component="form">
        <Grid
          container
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
          my={5}
          sx={{ backgroundColor: WsColors.White }}
          p={2}
        >
          <StyledStack sx={{ width: 200 }} spacing={1}>
            <WsButtonGroup
              inputTitle={'Ignore First Row Data'}
              buttonsList={['Yes', 'No']}
              selectedIndexValue={onChangeRow}
              defaultValue={0}
            />
          </StyledStack>
        </Grid>
        <WsDataTable
          columns={columns}
          data={parsedData as []}
          boldItems={['column1']}
          headless={false}
          preSelectedRows={[]}
        />

        <Stack spacing={1} width="100%" direction="row" justifyContent="end" mt={2}>
          <WsButton variant="contained" onClick={() => navigate('/consultant/')} size="large">
            Cancel
          </WsButton>

          <WsButton
            variant="contained"
            onClick={() => navigate('/clients/import-excel')}
            size="large"
          >
            Back
          </WsButton>

          <WsButton
            variant="contained"
            type="button"
            onClick={() => onSubmit(methods.getValues())}
            size="large"
          >
            Next
          </WsButton>
        </Stack>
      </Box>
    </FormProvider>
  );
};

export default MapSpreadsheetColumns;
